/**
 * Utilidades para trabajar con el tema corporativo
 */

import { Theme, alpha } from '@mui/material/styles';

/**
 * Obtiene el color de acento del tema
 */
export const getAccentColor = (theme: Theme) => theme.custom.colors.accent.main;

/**
 * Obtiene el color de acento secundario del tema
 */
export const getAccentSecondaryColor = (theme: Theme) => theme.custom.colors.accentSecondary.main;

/**
 * Obtiene el gradiente primario
 */
export const getPrimaryGradient = (theme: Theme) => theme.custom.gradients.primary;

/**
 * Obtiene el gradiente de acento
 */
export const getAccentGradient = (theme: Theme) => theme.custom.gradients.accent;

/**
 * Obtiene la ruta del logo según el contexto
 */
export const getLogoPath = (theme: Theme, variant: 'light' | 'dark' = 'dark') => {
  return variant === 'light' 
    ? theme.custom.logoPaths.light 
    : theme.custom.logoPaths.dark;
};

/**
 * Helper para crear estilos de botón con colores corporativos
 */
export const createButtonStyles = (theme: Theme, variant: 'primary' | 'accent' | 'accentSecondary' = 'primary') => {
  const colorMap = {
    primary: theme.palette.primary,
    accent: theme.custom.colors.accent,
    accentSecondary: theme.custom.colors.accentSecondary,
  };

  const color = colorMap[variant];

  return {
    backgroundColor: color.main,
    color: color.contrastText,
    '&:hover': {
      backgroundColor: color.dark,
      transform: 'translateY(-1px)',
      boxShadow: theme.shadows[4],
    },
    '&:active': {
      transform: 'translateY(0)',
    },
  };
};

/**
 * Helper para crear estilos de card con hover
 */
export const createCardStyles = (theme: Theme) => ({
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[2],
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: `1px solid ${theme.custom.colors.neutral.main}`,
  '&:hover': {
    boxShadow: theme.shadows[4],
    transform: 'translateY(-2px)',
  },
});

/**
 * Helper para crear estilos de input con focus en accent
 */
export const createInputStyles = (theme: Theme) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: (theme as any).custom?.shape?.borderRadius?.medium || 8,
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: (theme as any).custom?.colors?.accent?.main || theme.palette.primary.main,
      borderWidth: (theme as any).custom?.shape?.borderWidth?.medium || 2,
    },
  },
});

/**
 * Helper para crear estilos de Paper con backdrop blur (para formularios de auth)
 */
export const createAuthPaperStyles = (theme: Theme) => ({
  position: 'relative' as const,
  zIndex: 1,
  borderRadius: (theme as any).custom?.shape?.borderRadius?.large || 16,
  backgroundColor: theme.palette.background.paper,
  backdropFilter: 'blur(20px)',
  boxShadow: theme.shadows[8],
});

/**
 * Helper para crear estilos de contenedor de fondo con overlay
 */
export const createBackgroundContainerStyles = (
  theme: Theme,
  backgroundImage: string,
  useFallback: boolean,
  fallbackGradient: string
) => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative' as const,
  backgroundImage: useFallback ? fallbackGradient : `url(${backgroundImage})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: useFallback
      ? alpha(theme.palette.common.black, 0.25)
      : alpha(theme.palette.common.black, 0.45),
    zIndex: 0,
  },
});

/**
 * Helper para crear estilos de título con gradiente corporativo
 */
export const createGradientTitleStyles = (theme: Theme) => ({
  fontWeight: (theme as any).custom?.typography?.fontWeight?.bold || 700,
  letterSpacing: '-0.01em',
  background: (theme as any).custom?.gradients?.primary || theme.palette.primary.main,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
});

/**
 * Helper para crear estilos de botón de acción principal (accent)
 */
export const createPrimaryActionButtonStyles = (theme: Theme) => ({
  backgroundColor: theme.custom.colors.accent.main,
  color: theme.custom.colors.accent.contrastText,
  boxShadow: theme.shadows[3],
  '&:hover': {
    backgroundColor: theme.custom.colors.accent.dark,
    boxShadow: theme.shadows[6],
    transform: 'translateY(-1px)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
});

/**
 * Helper para crear estilos de icono con fondo de color (para cards del dashboard)
 */
export const createIconContainerStyles = (theme: Theme, color: 'accent' | 'success' | 'accentSecondary') => {
  const customTheme = theme as any;
  const colorMap = {
    accent: customTheme.custom?.colors?.accent?.main || theme.palette.primary.main,
    success: customTheme.custom?.colors?.success?.main || theme.palette.success.main,
    accentSecondary: customTheme.custom?.colors?.accentSecondary?.main || theme.palette.secondary.main,
  };

  return {
    p: 1.5,
    borderRadius: customTheme.custom?.shape?.borderRadius?.medium || 8,
    backgroundColor: colorMap[color],
    color: 'white',
    mr: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
};

/**
 * Helper para crear estilos de card con gradiente sutil
 */
export const createGradientCardStyles = (theme: Theme) => ({
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.custom.colors.neutral.light} 100%)`,
});





